// client/src/pages/PTO/IndividualReportModal.tsx
// Per-employee PTO summary modal — stats, type breakdown, monthly detail.

import React, { useMemo, useCallback } from 'react';
import { Printer, X } from 'lucide-react';
import type { PTOEntry, PTOEmployee, EntryType } from './ptoTypes';
import { ENTRY_TYPES } from './ptoConstants';
import { formatDate, getEntryTypeStyle, getOfficeColor } from './ptoUtils';

// ─── Props ──────────────────────────────────────────────────────────────────

interface IndividualReportModalProps {
  employee: PTOEmployee;
  entries: PTOEntry[];
  onClose: () => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const PTO_TYPES: Set<EntryType> = new Set([
  'PTO', 'Half Day', 'Sabbatical', 'Tentative PTO',
]);

// ─── Component ──────────────────────────────────────────────────────────────

const IndividualReportModal: React.FC<IndividualReportModalProps> = ({
  employee,
  entries,
  onClose,
}) => {
  // Filter entries for this employee
  const empEntries = useMemo(
    () =>
      entries
        .filter((e) => e.staffIndex === employee.staffIndex)
        .sort((a, b) => a.entryDate.localeCompare(b.entryDate)),
    [entries, employee.staffIndex],
  );

  // ── Stats ──────────────────────────────────────────────────────────────

  const totalPTOHours = useMemo(() => {
    return empEntries
      .filter((e) => PTO_TYPES.has(e.entryType))
      .reduce((sum, e) => sum + (e.hours ?? 8), 0);
  }, [empEntries]);

  const totalPTODays = Math.round((totalPTOHours / 8) * 10) / 10;

  const appointmentCount = useMemo(
    () => empEntries.filter((e) => e.entryType === 'Appointment').length,
    [empEntries],
  );

  // ── Type breakdown ────────────────────────────────────────────────────

  const typeBreakdown = useMemo(() => {
    const counts = new Map<EntryType, number>();
    for (const e of empEntries) {
      counts.set(e.entryType, (counts.get(e.entryType) ?? 0) + 1);
    }
    // Sort by ENTRY_TYPES order, only include types with count > 0
    return ENTRY_TYPES.filter((t) => counts.has(t.value)).map((t) => ({
      type: t.value,
      label: t.label,
      count: counts.get(t.value)!,
      bgColor: t.bgColor,
      textColor: t.textColor,
    }));
  }, [empEntries]);

  const maxTypeCount = useMemo(
    () => Math.max(1, ...typeBreakdown.map((t) => t.count)),
    [typeBreakdown],
  );

  // ── Monthly detail ────────────────────────────────────────────────────

  const monthlyGroups = useMemo(() => {
    const groups = new Map<number, PTOEntry[]>();
    for (const e of empEntries) {
      const month = Number(e.entryDate.slice(5, 7)) - 1;
      const list = groups.get(month) ?? [];
      list.push(e);
      groups.set(month, list);
    }
    // Return sorted by month
    return Array.from(groups.entries())
      .sort(([a], [b]) => a - b)
      .map(([month, list]) => ({ month, label: MONTH_NAMES[month], entries: list }));
  }, [empEntries]);

  // ── Print ─────────────────────────────────────────────────────────────

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // ── Avatar ────────────────────────────────────────────────────────────

  const initials = employee.displayName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const officeColor = getOfficeColor(employee.office);

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,.65)',
        backdropFilter: 'blur(4px)',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 620,
          maxHeight: '90vh',
          overflowY: 'auto',
          background: '#151820',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)',
        }}
      >
        {/* ── Header ────────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '18px 20px',
            borderBottom: '1px solid var(--border)',
            background: 'rgba(0,0,0,.12)',
          }}
        >
          {/* Avatar */}
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: `${officeColor}33`,
              color: officeColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {initials}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
              {employee.displayName}
            </div>
            <span
              style={{
                display: 'inline-block',
                marginTop: 3,
                padding: '2px 9px',
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 700,
                background: `${officeColor}33`,
                color: officeColor,
              }}
            >
              {employee.office}
            </span>
          </div>

          {/* Actions */}
          <button
            onClick={handlePrint}
            title="Print"
            style={{
              all: 'unset',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 6,
              border: '1px solid var(--border)',
              color: 'var(--muted)',
              transition: 'background .15s, color .15s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,.06)';
              (e.currentTarget as HTMLElement).style.color = 'var(--text)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
              (e.currentTarget as HTMLElement).style.color = 'var(--muted)';
            }}
          >
            <Printer size={15} />
          </button>
          <button
            onClick={onClose}
            title="Close"
            style={{
              all: 'unset',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 6,
              border: '1px solid var(--border)',
              color: 'var(--muted)',
              transition: 'background .15s, color .15s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,.06)';
              (e.currentTarget as HTMLElement).style.color = 'var(--text)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
              (e.currentTarget as HTMLElement).style.color = 'var(--muted)';
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* ── Body ──────────────────────────────────────────────── */}
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Summary stats */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 12,
            }}
          >
            {[
              { label: 'Total PTO Hours', value: totalPTOHours, color: 'var(--accent)' },
              { label: 'Total PTO Days', value: totalPTODays, color: 'var(--accent)' },
              { label: 'Appointments', value: appointmentCount, color: 'var(--warn)' },
            ].map((stat) => (
              <div
                key={stat.label}
                style={{
                  textAlign: 'center',
                  padding: '14px 10px',
                  borderRadius: 10,
                  background: 'rgba(255,255,255,.03)',
                  border: '1px solid var(--border)',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '.5px',
                    color: 'var(--muted)',
                    marginBottom: 6,
                  }}
                >
                  {stat.label}
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, color: stat.color }}>
                  {stat.value}
                </div>
              </div>
            ))}
          </div>

          {/* Type breakdown */}
          {typeBreakdown.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '.5px',
                  marginBottom: 10,
                }}
              >
                Entry Type Breakdown
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {typeBreakdown.map((tb) => (
                  <div
                    key={tb.type}
                    style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                  >
                    <span
                      style={{
                        width: 90,
                        fontSize: 11,
                        fontWeight: 600,
                        color: tb.textColor,
                        flexShrink: 0,
                      }}
                    >
                      {tb.label}
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: 18,
                        background: 'rgba(255,255,255,.03)',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${(tb.count / maxTypeCount) * 100}%`,
                          height: '100%',
                          background: tb.bgColor,
                          borderRadius: 4,
                          minWidth: 20,
                          transition: 'width .3s',
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: 'var(--text)',
                        width: 30,
                        textAlign: 'right',
                        flexShrink: 0,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {tb.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Monthly detail */}
          {monthlyGroups.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '.5px',
                  marginBottom: 10,
                }}
              >
                Monthly Detail
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {monthlyGroups.map(({ month, label, entries: monthEntries }) => (
                  <div key={month}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: 'var(--text)',
                        padding: '6px 0',
                        borderBottom: '1px solid var(--border)',
                        marginBottom: 6,
                      }}
                    >
                      {label}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {monthEntries.map((entry) => {
                        const style = getEntryTypeStyle(entry.entryType);
                        return (
                          <div
                            key={entry.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '5px 0',
                              fontSize: 12,
                            }}
                          >
                            <span
                              style={{
                                color: 'var(--muted)',
                                fontSize: 12,
                                minWidth: 70,
                                flexShrink: 0,
                              }}
                            >
                              {formatDate(entry.entryDate)}
                            </span>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: 10,
                                fontSize: 10,
                                fontWeight: 700,
                                background: style.bg,
                                color: style.color,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {entry.entryType}
                            </span>
                            {entry.hours != null && (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: 'var(--text)',
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {entry.hours}h
                              </span>
                            )}
                            {entry.note && (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: 'var(--muted)',
                                  fontStyle: 'italic',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {entry.note}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {empEntries.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '32px 16px',
                color: 'var(--muted)',
                fontSize: 13,
              }}
            >
              No entries for this employee.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IndividualReportModal;
